/** Spacing values offered above and below a paragraph, in points as in Word. */
export const PARAGRAPH_SPACING_VALUES = [
  '0pt',
  '3pt',
  '6pt',
  '8pt',
  '12pt',
  '18pt',
  '24pt'
];

const SIDES = { before: 'margin-top', after: 'margin-bottom' };

// The blocks a paragraph spacing makes sense on; table cells ignore margins.
const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'LI',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'BLOCKQUOTE',
  'PRE',
  'DD',
  'DT'
]);
const BLOCK_SELECTOR = Array.from(BLOCK_TAGS).join(',');

// `0px` (Word imports), `0pt` and `0` are the same spacing.
const normalizeSpacing = (value) =>
  parseFloat(value) === 0 ? '0' : (value || '').trim();

const closestBlock = (node, root) => {
  let current = node.nodeType === 3 ? node.parentElement : node;
  while (current && current !== root) {
    if (BLOCK_TAGS.has(current.tagName)) return current;
    current = current.parentElement;
  }
  return null;
};

/**
 * Lists the deepest blocks the selection touches, or the caret's block when
 * the selection is collapsed.
 * @function
 *
 * @param {Object} editor - The Jodit editor instance.
 * @returns {HTMLElement[]} The blocks, possibly none.
 */
export const selectedBlocks = (editor) => {
  const root = editor?.editor;
  const range = editor?.s?.range;
  if (!root || !range) return [];

  if (!range.collapsed) {
    const blocks = Array.from(root.querySelectorAll(BLOCK_SELECTOR)).filter(
      (block) =>
        range.intersectsNode(block) &&
        !Array.from(block.querySelectorAll(BLOCK_SELECTOR)).some((inner) =>
          range.intersectsNode(inner)
        )
    );
    if (blocks.length) return blocks;
  }

  const block = closestBlock(range.startContainer, root);
  return block ? [block] : [];
};

/**
 * Reads the spacing shared by every selected block on that side.
 * @function
 *
 * @param {Object} editor - The Jodit editor instance.
 * @param {string} side - `before` or `after`.
 * @returns {string|null} The normalised value, or null when unset or mixed.
 */
export const currentParagraphSpacing = (editor, side) => {
  const property = SIDES[side];
  if (!property) return null;
  const blocks = selectedBlocks(editor);
  if (!blocks.length) return null;

  const values = new Set(
    blocks.map((block) =>
      normalizeSpacing(block.style.getPropertyValue(property))
    )
  );
  const [value] = values;
  return values.size === 1 && value !== '' ? value : null;
};

/**
 * Tells whether every selected block has that spacing on that side.
 * @function
 *
 * @param {Object} editor - The Jodit editor instance.
 * @param {string} side - `before` or `after`.
 * @param {string} value - A spacing such as `8pt`.
 * @returns {boolean} True when it matches on every selected block.
 */
export const hasParagraphSpacing = (editor, side, value) =>
  Boolean(value) &&
  currentParagraphSpacing(editor, side) === normalizeSpacing(value);

/**
 * Sets the spacing on that side of the selected blocks, inline so that the
 * PDF prints it alike. Zero is written explicitly.
 * @function
 *
 * @param {Object} editor - The Jodit editor instance.
 * @param {string} side - `before` or `after`.
 * @param {string} value - A spacing such as `8pt`.
 * @returns {boolean} True when the document was changed.
 */
export const setParagraphSpacing = (editor, side, value) => {
  const property = SIDES[side];
  if (!property || !value) return false;
  const blocks = selectedBlocks(editor);
  if (!blocks.length) return false;

  blocks.forEach((block) => block.style.setProperty(property, value));
  editor.synchronizeValues();
  return true;
};
